<template>
  <div class="bs-page-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white; font-weight: 600;">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- 1. HERO HEADER SECTION -->
    <header class="bs-hero-header mb-4">
      <div class="bs-hero-text">
        <PageBreadcrumb :items="[{ text: 'Accounts', to: '/Accounts' }, { text: 'Balance Sheet' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <div class="date-badge-pill">
            <v-icon small color="#475569" class="mr-1">mdi-calendar-check-outline</v-icon>
            <span>As of {{ formatDateDisplay(asOf) }}</span>
          </div>
          <div
            class="status-badge-pill"
            :class="isBalanced ? 'pill-balanced' : 'pill-warning'"
          >
            <v-icon small :color="isBalanced ? '#166534' : '#991b1b'" class="mr-1">
              {{ isBalanced ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            <span>{{ isBalanced ? 'Balanced' : 'Variance Detected' }}</span>
          </div>
        </div>
        <p class="bs-subtitle">
          Statement of financial position: assets, liabilities, and equity
        </p>
      </div>

      <div class="bs-hero-actions">
        <button class="action-btn outline-btn mr-2" @click="fetchBalanceSheet()" title="Refresh Statement">
          <v-icon left size="16" color="#334155">mdi-refresh</v-icon>
          Refresh
        </button>
        <button
          class="action-btn primary-dark-btn"
          :disabled="isDownloading"
          @click="download()"
        >
          <v-icon left size="17" color="#ffffff">
            {{ isDownloading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}
          </v-icon>
          {{ isDownloading ? 'Exporting...' : 'Download PDF' }}
        </button>
      </div>
    </header>

    <!-- 2. DATE FILTER CONTROLS & PRESETS STRIP -->
    <section class="bs-filter-card mb-4">
      <div class="filter-card-left">
        <div class="preset-label-wrap">
          <v-icon size="16" color="#64748b" class="mr-1">mdi-calendar-range</v-icon>
          <span class="preset-title">Reporting Date:</span>
        </div>
        <div class="preset-chips-wrap">
          <button
            v-for="preset in presets"
            :key="preset.id"
            class="preset-chip"
            :class="{ active: activePreset === preset.id }"
            @click="applyPreset(preset.id)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>

      <div class="filter-card-right">
        <div class="date-field-wrapper">
          <span class="date-label">As of Date:</span>
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="asOf"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="custom-date-box" v-bind="attrs" v-on="on">
                <v-icon size="15" color="#475569" class="mr-1">mdi-calendar</v-icon>
                <span class="date-box-val">{{ asOf }}</span>
                <v-icon size="14" color="#94a3b8" class="ml-1">mdi-chevron-down</v-icon>
              </div>
            </template>
            <v-date-picker
              v-model="asOf"
              color="#1e293b"
              no-title
              scrollable
              @change="onDateChange(asOf)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>
      </div>
    </section>

    <!-- 3. TOP EXECUTIVE KPI METRICS (4 CARDS) -->
    <section class="bs-kpi-grid mb-4">
      <!-- 1. Total Assets -->
      <div class="bs-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-bank-outline</v-icon>
          </div>
          <span class="kpi-pill">Assets</span>
        </div>
        <div class="kpi-label">TOTAL ASSETS</div>
        <div class="kpi-value">₹ {{ formatCurrency(totalAssets) }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            {{ assetRows.length }} asset {{ assetRows.length === 1 ? 'account' : 'accounts' }}
          </span>
        </div>
      </div>

      <!-- 2. Total Liabilities -->
      <div class="bs-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-hand-coin-outline</v-icon>
          </div>
          <span class="kpi-pill">Liabilities</span>
        </div>
        <div class="kpi-label">TOTAL LIABILITIES</div>
        <div class="kpi-value">₹ {{ formatCurrency(totalLiabilities) }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            {{ liabilityRows.length }} liability {{ liabilityRows.length === 1 ? 'account' : 'accounts' }}
          </span>
        </div>
      </div>

      <!-- 3. Capital & Equity -->
      <div class="bs-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-shield-check-outline</v-icon>
          </div>
          <span class="kpi-pill">Equity</span>
        </div>
        <div class="kpi-label">TOTAL CAPITAL &amp; EQUITY</div>
        <div class="kpi-value">₹ {{ formatCurrency(totalCapital) }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            {{ capitalRows.length }} equity &amp; profit {{ capitalRows.length === 1 ? 'entry' : 'entries' }}
          </span>
        </div>
      </div>

      <!-- 4. Accounting Equation Balance / Solvency -->
      <div class="bs-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" :color="isBalanced ? '#166534' : '#991b1b'">
              {{ isBalanced ? 'mdi-scale-balance' : 'mdi-scale-unbalanced' }}
            </v-icon>
          </div>
          <span
            class="kpi-pill"
            :class="isBalanced ? 'pill-balanced' : 'pill-warning'"
          >
            {{ isBalanced ? 'In Balance' : 'Variance' }}
          </span>
        </div>
        <div class="kpi-label">LIABILITIES + CAPITAL</div>
        <div class="kpi-value">₹ {{ formatCurrency(totalLiabilitiesAndCapital) }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            <v-icon size="14" :color="isBalanced ? '#166534' : '#991b1b'" class="mr-1">
              {{ isBalanced ? 'mdi-check' : 'mdi-alert' }}
            </v-icon>
            {{ isBalanced ? 'Assets = Liabilities + Equity' : 'Out of balance' }}
          </span>
        </div>
      </div>
    </section>

    <!-- 4. ACCOUNTING EQUATION PROPORTION BAR (SUBTLE & PROFESSIONAL) -->
    <section class="bs-comparison-strip mb-4" v-if="totalAssets > 0 || totalLiabilitiesAndCapital > 0">
      <div class="comparison-bar-header">
        <div class="bar-legend-item">
          <span class="legend-indicator dot-assets"></span>
          <span class="legend-name">Total Assets:</span>
          <span class="legend-val">₹ {{ formatCurrency(totalAssets) }}</span>
        </div>
        <div class="bar-legend-center">
          <span class="equation-text">
            Assets = Liabilities ({{ liabPct }}%) + Equity ({{ capitalPct }}%)
          </span>
        </div>
        <div class="bar-legend-item">
          <span class="legend-indicator dot-funding"></span>
          <span class="legend-name">Liabilities &amp; Capital:</span>
          <span class="legend-val">₹ {{ formatCurrency(totalLiabilitiesAndCapital) }}</span>
        </div>
      </div>

      <div class="equation-dual-track">
        <div class="track-half">
          <div class="track-bar bar-assets" style="width: 100%" title="Assets: 100%"></div>
        </div>
        <div class="track-divider">
          <span>=</span>
        </div>
        <div class="track-half">
          <div
            class="track-bar bar-liab"
            :style="{ width: liabPct + '%' }"
            :title="'Liabilities: ' + liabPct + '%'"
          ></div>
          <div
            class="track-bar bar-equity"
            :style="{ width: capitalPct + '%' }"
            :title="'Equity: ' + capitalPct + '%'"
          ></div>
        </div>
      </div>
    </section>

    <!-- Variance alert if out of balance -->
    <section class="mb-4" v-if="!isBalanced">
      <div class="variance-alert-box">
        <v-icon size="20" color="#991b1b" class="mr-3">mdi-alert-circle-outline</v-icon>
        <div class="alert-content">
          <h4 class="alert-title">Statement Variance Detected</h4>
          <p class="alert-message">
            Assets (₹ {{ formatCurrency(totalAssets) }}) do not equal Liabilities + Capital (₹ {{ formatCurrency(totalLiabilitiesAndCapital) }}).
            Discrepancy: ₹ {{ formatCurrency(Math.abs(totalAssets - totalLiabilitiesAndCapital)) }}.
            Please review that opening balances were entered as a balanced set.
          </p>
        </div>
      </div>
    </section>

    <!-- 5. MAIN SPLIT TABLES (ASSETS VS LIABILITIES & CAPITAL) -->
    <section class="bs-tables-grid mb-4">
      <!-- Left: Assets Table Card -->
      <div class="financial-card">
        <div class="card-top-bar">
          <div class="bar-title-wrap">
            <h2 class="head-card-title">Assets</h2>
            <span class="head-count-badge">{{ assetRows.length }}</span>
          </div>

          <div class="bar-controls-wrap">
            <div class="mini-search-box">
              <v-icon size="14" color="#94a3b8">mdi-magnify</v-icon>
              <input
                v-model="assetSearch"
                type="text"
                placeholder="Filter asset..."
                class="mini-search-input"
              />
              <v-icon
                v-if="assetSearch"
                size="14"
                color="#94a3b8"
                class="cursor-pointer"
                @click="assetSearch = ''"
              >
                mdi-close
              </v-icon>
            </div>

            <button
              class="mini-sort-btn"
              @click="toggleAssetSort()"
              :title="'Sort: ' + assetSort"
            >
              <v-icon size="15" color="#475569">
                {{ assetSort === 'amount-desc' ? 'mdi-sort-numeric-descending' : 'mdi-sort-alphabetical-ascending' }}
              </v-icon>
            </button>
          </div>
        </div>

        <div class="table-scroll-container">
          <table class="financial-statement-table">
            <thead>
              <tr>
                <th class="text-left">Asset Head</th>
                <th class="text-center" style="width: 90px;">Share</th>
                <th class="text-right" style="width: 150px;">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in filteredAssetRows" :key="item._id || i" class="table-row">
                <td class="text-left">
                  <span class="account-name-text">{{ item.name }}</span>
                </td>
                <td class="text-center">
                  <span class="share-badge">
                    {{ calculateShare(item.amount, totalAssets) }}%
                  </span>
                </td>
                <td class="text-right num-cell font-weight-bold">
                  ₹ {{ formatCurrency(item.amount) }}
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredAssetRows.length === 0">
                <td colspan="3" class="empty-state-cell">
                  <div class="empty-state-inner">
                    <v-icon size="30" color="#94a3b8" class="mb-2">mdi-folder-open-outline</v-icon>
                    <p class="empty-title">
                      {{ assetSearch ? 'No matching assets found' : 'No asset accounts recorded' }}
                    </p>
                    <p class="empty-desc">
                      {{ assetSearch ? 'Try a different search query.' : 'No asset head balances recorded as of this date.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="summary-footer-row">
                <td class="text-left footer-label">TOTAL ASSETS</td>
                <td class="text-center">
                  <span class="footer-count-chip">{{ assetRows.length }} Heads</span>
                </td>
                <td class="text-right footer-amount num-cell">
                  ₹ {{ formatCurrency(totalAssets) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Right: Liabilities & Capital Table Card -->
      <div class="financial-card">
        <div class="card-top-bar">
          <div class="bar-title-wrap">
            <h2 class="head-card-title">Liabilities &amp; Capital</h2>
            <span class="head-count-badge">{{ liabilityRows.length + capitalRows.length }}</span>
          </div>

          <div class="bar-controls-wrap">
            <div class="sub-tab-pill-group">
              <button
                class="sub-tab-btn"
                :class="{ active: liabCapTab === 'all' }"
                @click="liabCapTab = 'all'"
              >
                All
              </button>
              <button
                class="sub-tab-btn"
                :class="{ active: liabCapTab === 'liability' }"
                @click="liabCapTab = 'liability'"
              >
                Liabilities
              </button>
              <button
                class="sub-tab-btn"
                :class="{ active: liabCapTab === 'capital' }"
                @click="liabCapTab = 'capital'"
              >
                Capital
              </button>
            </div>

            <div class="mini-search-box">
              <v-icon size="14" color="#94a3b8">mdi-magnify</v-icon>
              <input
                v-model="liabCapSearch"
                type="text"
                placeholder="Filter head..."
                class="mini-search-input"
              />
              <v-icon
                v-if="liabCapSearch"
                size="14"
                color="#94a3b8"
                class="cursor-pointer"
                @click="liabCapSearch = ''"
              >
                mdi-close
              </v-icon>
            </div>

            <button
              class="mini-sort-btn"
              @click="toggleLiabCapSort()"
              :title="'Sort: ' + liabCapSort"
            >
              <v-icon size="15" color="#475569">
                {{ liabCapSort === 'amount-desc' ? 'mdi-sort-numeric-descending' : 'mdi-sort-alphabetical-ascending' }}
              </v-icon>
            </button>
          </div>
        </div>

        <div class="table-scroll-container">
          <table class="financial-statement-table">
            <thead>
              <tr>
                <th class="text-left">Head Name</th>
                <th class="text-center" style="width: 90px;">Share</th>
                <th class="text-right" style="width: 150px;">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in filteredLiabCapRows" :key="item.categoryType + '-' + (item._id || i)" class="table-row">
                <td class="text-left">
                  <div class="d-flex align-center">
                    <span class="account-name-text">{{ item.name }}</span>
                    <span
                      class="type-tag"
                      :class="getTypeTagClass(item)"
                    >
                      {{ getTypeTagLabel(item) }}
                    </span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="share-badge">
                    {{ calculateShare(item.amount, totalLiabilitiesAndCapital) }}%
                  </span>
                </td>
                <td class="text-right num-cell font-weight-bold">
                  ₹ {{ formatCurrency(item.amount) }}
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredLiabCapRows.length === 0">
                <td colspan="3" class="empty-state-cell">
                  <div class="empty-state-inner">
                    <v-icon size="30" color="#94a3b8" class="mb-2">mdi-scale-balance</v-icon>
                    <p class="empty-title">
                      {{ liabCapSearch ? 'No matching accounts' : 'No liability or capital accounts recorded' }}
                    </p>
                    <p class="empty-desc">
                      {{ liabCapSearch ? 'Try clearing your search query.' : 'No balances recorded as of this date.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="summary-footer-row">
                <td class="text-left footer-label">TOTAL LIABILITIES &amp; CAPITAL</td>
                <td class="text-center">
                  <span class="footer-count-chip">{{ liabilityRows.length + capitalRows.length }} Heads</span>
                </td>
                <td class="text-right footer-amount num-cell">
                  ₹ {{ formatCurrency(totalLiabilitiesAndCapital) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>

    <!-- 6. GRAND EXECUTIVE SUMMARY RIBBON (CLEAN & SUBTLE) -->
    <section class="grand-summary-banner">
      <div class="banner-left">
        <div class="banner-icon-container">
          <v-icon size="24" :color="isBalanced ? '#166534' : '#991b1b'">
            {{ isBalanced ? 'mdi-check-decagram-outline' : 'mdi-alert-outline' }}
          </v-icon>
        </div>
        <div class="banner-text">
          <div class="banner-badge-line">
            <span
              class="banner-badge"
              :class="isBalanced ? 'badge-balanced' : 'badge-warning'"
            >
              {{ isBalanced ? 'DOUBLE-ENTRY BALANCED' : 'VARIANCE DETECTED' }}
            </span>
            <span class="banner-margin-tag">
              Net Worth: ₹ {{ formatCurrency(totalCapital) }}
            </span>
          </div>
          <p class="banner-formula-text">
            Total Assets (₹ {{ formatCurrency(totalAssets) }}) = Total Liabilities (₹ {{ formatCurrency(totalLiabilities) }}) + Capital &amp; Equity (₹ {{ formatCurrency(totalCapital) }})
          </p>
        </div>
      </div>

      <div class="banner-right">
        <span class="banner-amount-label">TOTAL VALUE</span>
        <div class="banner-amount-val num-cell">
          ₹ {{ formatCurrency(totalAssets) }}
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BalanceSheet",
  data() {
    var today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10);
    return {
      appLoading: false,
      isDownloading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      menu: false,
      asOf: today,
      activePreset: "today",
      presets: [
        { id: "today", label: "Today" },
        { id: "lastMonth", label: "End of Last Month" },
        { id: "lastQuarter", label: "End of Last Quarter" },
        { id: "fyEnd", label: "FY-End (31 Mar)" },
        { id: "fyStart", label: "FY-Start (1 Apr)" },
      ],
      assetRows: [],
      liabilityRows: [],
      capitalRows: [],
      totalAssets: 0,
      totalLiabilities: 0,
      totalCapital: 0,
      totalLiabilitiesAndCapital: 0,
      isBalanced: true,
      assetSearch: "",
      liabCapSearch: "",
      assetSort: "amount-desc",
      liabCapSort: "amount-desc",
      liabCapTab: "all", // 'all', 'liability', 'capital'
    };
  },
  computed: {
    filteredAssetRows() {
      let rows = [...this.assetRows];
      if (this.assetSearch && this.assetSearch.trim()) {
        const q = this.assetSearch.toLowerCase().trim();
        rows = rows.filter((r) => r.name && r.name.toLowerCase().includes(q));
      }
      if (this.assetSort === "amount-desc") {
        rows.sort((a, b) => b.amount - a.amount);
      } else if (this.assetSort === "name-asc") {
        rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      return rows;
    },
    combinedLiabCapRows() {
      const list = [];
      this.liabilityRows.forEach((item) => {
        list.push({ ...item, categoryType: "Liability" });
      });
      this.capitalRows.forEach((item) => {
        list.push({ ...item, categoryType: "Capital" });
      });
      return list;
    },
    filteredLiabCapRows() {
      let rows = [...this.combinedLiabCapRows];
      if (this.liabCapTab === "liability") {
        rows = rows.filter((r) => r.categoryType === "Liability");
      } else if (this.liabCapTab === "capital") {
        rows = rows.filter((r) => r.categoryType === "Capital");
      }
      if (this.liabCapSearch && this.liabCapSearch.trim()) {
        const q = this.liabCapSearch.toLowerCase().trim();
        rows = rows.filter((r) => r.name && r.name.toLowerCase().includes(q));
      }
      if (this.liabCapSort === "amount-desc") {
        rows.sort((a, b) => b.amount - a.amount);
      } else if (this.liabCapSort === "name-asc") {
        rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      return rows;
    },
    liabPct() {
      const total = this.totalLiabilitiesAndCapital || 0;
      if (total <= 0) return 0;
      return Math.round(((this.totalLiabilities || 0) / total) * 100);
    },
    capitalPct() {
      const total = this.totalLiabilitiesAndCapital || 0;
      if (total <= 0) return 100;
      return 100 - this.liabPct;
    },
  },
  mounted() {
    this.fetchBalanceSheet();
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val) || val === "") return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDateDisplay(dateStr) {
      if (!dateStr) return "";
      try {
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          const d = new Date(parts[0], parts[1] - 1, parts[2]);
          return d.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        }
        return dateStr;
      } catch (e) {
        return dateStr;
      }
    },
    calculateShare(amount, total) {
      if (!total || total <= 0 || !amount) return "0.0";
      return ((amount / total) * 100).toFixed(1);
    },
    getTypeTagClass(item) {
      if (item.name === "Current Profit") return "tag-profit";
      if (item.name === "Current Loss") return "tag-loss";
      if (item.categoryType === "Liability") return "tag-liability";
      return "tag-capital";
    },
    getTypeTagLabel(item) {
      if (item.name === "Current Profit") return "Retained Profit";
      if (item.name === "Current Loss") return "Operating Loss";
      if (item.categoryType === "Liability") return "Liability";
      return "Equity";
    },
    applyPreset(type) {
      this.activePreset = type;
      const now = new Date();
      const formatYMD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      if (type === "today") {
        this.asOf = formatYMD(now);
      } else if (type === "lastMonth") {
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        this.asOf = formatYMD(endOfLastMonth);
      } else if (type === "lastQuarter") {
        const curQuarter = Math.floor(now.getMonth() / 3);
        const endOfLastQuarter = new Date(now.getFullYear(), curQuarter * 3, 0);
        this.asOf = formatYMD(endOfLastQuarter);
      } else if (type === "fyEnd") {
        const curYear = now.getFullYear();
        const curMonth = now.getMonth();
        const fyEndYear = curMonth >= 2 ? curYear : curYear - 1;
        const endOfFY = new Date(fyEndYear, 2, 31);
        this.asOf = formatYMD(endOfFY);
      } else if (type === "fyStart") {
        const curYear = now.getFullYear();
        const curMonth = now.getMonth();
        const fyStartYear = curMonth >= 3 ? curYear : curYear - 1;
        const startOfFY = new Date(fyStartYear, 3, 1);
        this.asOf = formatYMD(startOfFY);
      }
      this.fetchBalanceSheet();
    },
    onDateChange(val) {
      if (this.$refs.menu) {
        this.$refs.menu.save(val);
      }
      this.activePreset = null;
      this.fetchBalanceSheet();
    },
    toggleAssetSort() {
      this.assetSort = this.assetSort === "amount-desc" ? "name-asc" : "amount-desc";
    },
    toggleLiabCapSort() {
      this.liabCapSort = this.liabCapSort === "amount-desc" ? "name-asc" : "amount-desc";
    },
    fetchBalanceSheet() {
      if (!this.asOf) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/accounting/balancesheet",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          asOf: this.asOf,
        },
      })
        .then((res) => {
          this.appLoading = false;
          if (res.data.status) {
            this.assetRows = res.data.assetRows || [];
            this.liabilityRows = res.data.liabilityRows || [];
            this.capitalRows = res.data.capitalRows || [];
            this.totalAssets = res.data.totalAssets || 0;
            this.totalLiabilities = res.data.totalLiabilities || 0;
            this.totalCapital = res.data.totalCapital || 0;
            this.totalLiabilitiesAndCapital = res.data.totalLiabilitiesAndCapital || 0;
            this.isBalanced = res.data.isBalanced !== false;
          } else {
            this.msg = res.data.msg || "Failed to load Balance Sheet";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    download() {
      if (!this.asOf) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.isDownloading = true;
      axios({
        method: "get",
        url: "/download/balancesheet",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          asOf: this.asOf,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.isDownloading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `BalanceSheet_AsOf_${this.asOf}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.isDownloading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
/* Page Layout Container */
.bs-page-container {
  padding: 4px 0 40px 0;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
}

/* 1. Hero Header Section */
.bs-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.bs-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.bs-main-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
}

.date-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.status-badge-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
}

.pill-balanced {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.pill-warning {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.bs-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.bs-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.primary-dark-btn {
  background: #1e293b;
  color: #ffffff;
  border: 1px solid #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.primary-dark-btn:hover:not(:disabled) {
  background: #0f172a;
  border-color: #0f172a;
}

.primary-dark-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 2. Filter Strip */
.bs-filter-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 16px;
}

.filter-card-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.preset-label-wrap {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.preset-chips-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.preset-chip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.preset-chip:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #cbd5e1;
}

.preset-chip.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
}

.filter-card-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-field-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.custom-date-box {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s ease;
}

.custom-date-box:hover {
  border-color: #64748b;
}

.date-box-val {
  min-width: 82px;
  text-align: center;
}

/* 3. KPI Grid */
.bs-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

@media (max-width: 1100px) {
  .bs-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .bs-kpi-grid {
    grid-template-columns: 1fr;
  }
}

.bs-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: border-color 0.15s ease;
}

.bs-kpi-card:hover {
  border-color: #cbd5e1;
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.kpi-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-pill {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
  line-height: 1.2;
  margin-bottom: 8px;
  color: #0f172a;
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
}

.num-cell {
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
  color: #0f172a;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}

.kpi-meta {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
}

/* 4. Comparison Strip (Subtle) */
.bs-comparison-strip {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 18px;
}

.comparison-bar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
}

.bar-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
}

.legend-indicator {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  display: inline-block;
}

.dot-assets {
  background: #334155;
}

.dot-funding {
  background: #64748b;
}

.legend-name {
  color: #64748b;
}

.legend-val {
  font-weight: 700;
  color: #0f172a;
}

.equation-text {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 2px 10px;
  border-radius: 4px;
}

.equation-dual-track {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.track-half {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  background: #f1f5f9;
}

.track-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  width: 16px;
}

.track-bar {
  height: 100%;
}

.bar-assets {
  background: #334155;
}

.bar-liab {
  background: #94a3b8;
}

.bar-equity {
  background: #475569;
}

/* Variance alert */
.variance-alert-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
}

.alert-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #991b1b;
  margin: 0 0 2px 0;
}

.alert-message {
  font-size: 12px;
  color: #7f1d1d;
  margin: 0;
  line-height: 1.4;
}

/* 5. Split Tables Grid */
.bs-tables-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 960px) {
  .bs-tables-grid {
    grid-template-columns: 1fr;
  }
}

.financial-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-top-bar {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 10px;
}

.bar-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.head-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.2px;
}

.head-count-badge {
  font-size: 11px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 1px 6px;
  border-radius: 4px;
}

.bar-controls-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.sub-tab-pill-group {
  display: inline-flex;
  background: #e2e8f0;
  padding: 2px;
  border-radius: 6px;
}

.sub-tab-btn {
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}

.sub-tab-btn.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.mini-search-box {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 8px;
  width: 140px;
  gap: 4px;
  transition: border-color 0.15s ease;
}

.mini-search-box:focus-within {
  border-color: #0f172a;
}

.mini-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 11.5px;
  color: #0f172a;
  width: 100%;
}

.mini-sort-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mini-sort-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.table-scroll-container {
  overflow-x: auto;
  max-height: 480px;
  overflow-y: auto;
}

.financial-statement-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.financial-statement-table th {
  background: #ffffff;
  color: #64748b;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
}

.financial-statement-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: middle;
}

.table-row:hover {
  background-color: #f8fafc;
}

.account-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13px;
}

.type-tag {
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 8px;
  display: inline-block;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.tag-profit {
  background: #f0fdf4;
  color: #166534;
  border-color: #bbf7d0;
}

.tag-loss {
  background: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}

.share-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-block;
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

/* Empty state */
.empty-state-cell {
  padding: 36px 16px !important;
  text-align: center;
  background: #ffffff;
}

.empty-state-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 2px 0;
}

.empty-desc {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

/* Summary Footer */
.summary-footer-row {
  border-top: 2px solid #cbd5e1;
  background: #f8fafc;
  font-weight: 700;
}

.footer-label {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #0f172a;
}

.footer-count-chip {
  font-size: 11px;
  font-weight: 600;
  background: #ffffff;
  padding: 1px 6px;
  border-radius: 4px;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.footer-amount {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

/* 6. Grand Executive Summary Ribbon */
.grand-summary-banner {
  border-radius: 10px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.banner-icon-container {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-badge-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.banner-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
}

.badge-balanced {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.badge-warning {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.banner-margin-tag {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
}

.banner-formula-text {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  margin: 0;
}

.banner-right {
  text-align: right;
}

.banner-amount-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  display: block;
  margin-bottom: 2px;
}

.banner-amount-val {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.1;
  color: #0f172a;
}

/* Print Styles */
@media print {
  .bs-hero-actions,
  .bs-filter-card,
  .sub-tab-pill-group,
  .mini-search-box,
  .mini-sort-btn {
    display: none !important;
  }

  .bs-page-container {
    padding: 0 !important;
    max-width: 100% !important;
  }

  .financial-card {
    box-shadow: none !important;
    border: 1px solid #ccc !important;
  }

  .table-scroll-container {
    max-height: none !important;
    overflow: visible !important;
  }

  .grand-summary-banner {
    box-shadow: none !important;
  }
}
</style>
